import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Clock, ChefHat, Pill, HeartPulse, ClipboardList, Salad, Dumbbell, Activity, FlaskConical, UserCircle } from 'lucide-react';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
interface Session {
  name: string;
  duration: string;
  description: string;
  badge?: string;
  highlight?: boolean;
}
const followUps: Session[] = [{
  name: '30-Minute Follow-Up',
  duration: 'Single Session',
  description: "A focused check-in to fine-tune your plan, troubleshoot what's working, and keep momentum on track."
}, {
  name: '30-Minute Follow-Up · 3 Pack',
  duration: '3 Sessions',
  description: 'Best for anyone working toward a 2-3 month goal who wants accountability and consistent course-correction.'
}, {
  name: '30-Minute Follow-Up · 5 Pack',
  duration: '5 Sessions',
  description: 'Highly recommended if you have 20+ pounds to lose or are navigating a longer GLP-1 protocol.',
  badge: 'Popular',
  highlight: true
}, {
  name: '30-Minute Follow-Up · 10 Pack',
  duration: '10 Sessions',
  description: 'For sustained transformation — keeps you focused, motivated, and progressing toward long-term goals.'
}];
const classes = [{
  icon: ChefHat,
  title: 'Cooking Classes',
  description: "Hands-on, virtual or in-person sessions focused on high-protein, GLP-1-friendly meals you'll actually want to eat. Learn techniques, batch-prep strategies, and flavor-building principles.",
  format: 'Group or Private',
  duration: '60–90 min'
}, {
  icon: Pill,
  title: 'Supplement Education',
  description: "A deep-dive class on building a smart supplement protocol. We'll cover the essentials, what's actually worth your money, and how to evaluate quality and third-party testing.",
  format: 'Group or 1:1',
  duration: '60 min'
}, {
  icon: HeartPulse,
  title: 'Diabetes Education',
  description: 'Comprehensive class on managing blood sugar through nutrition, lifestyle, and medication coordination. Ideal for prediabetes, type 2 diabetes, or anyone wanting to optimize metabolic health.',
  format: 'Group or 1:1',
  duration: '75 min'
}];
interface SessionCardProps {
  title: string;
  duration: string;
  description: string;
  badge?: string;
  highlight?: boolean;
  variant?: 'light' | 'dark';
}
function SessionCard({
  title,
  duration,
  description,
  badge,
  highlight,
  variant = 'light'
}: SessionCardProps) {
  const isDark = variant === 'dark';
  return <div className={`relative flex flex-col h-full p-8 border transition-all ${highlight ? 'bg-tealslate text-white border-tealslate' : isDark ? 'bg-navyslate/30 border-darkgray hover:border-tealslate/60 text-white' : 'bg-white border-[#D8D8D0] hover:border-tealslate/60 text-[#1F1F1F]'}`} data-id="element-2952" data-mp="3m19eslsqbuoo">
      {badge && <span className={`absolute -top-3 left-8 text-[10px] font-bold uppercase tracking-widest px-3 py-1 ${highlight ? 'bg-burgundy text-white' : 'bg-tealslate text-white'}`} data-id="element-2953" data-mp="30oiu1v1oxpko">
          {badge}
        </span>}

      <div className="flex items-center gap-2 mb-4" data-id="element-2954" data-mp="3sgqcn10rbc1p">
        <Clock className={`w-4 h-4 ${highlight ? 'text-white/70' : isDark ? 'text-tealslate' : 'text-tealslate'}`} data-id="element-2955" data-mp="3odtmmp8w0dnq" />
        <span className={`text-[10px] font-bold uppercase tracking-widest ${highlight ? 'text-white/70' : isDark ? 'text-[#9CA3AF]' : 'text-[#7A7A70]'}`} data-id="element-2956" data-mp="3jdp6j0bw5ky1">
          {duration}
        </span>
      </div>

      <h3 className={`text-2xl font-serif mb-4 leading-tight ${highlight ? 'text-white' : isDark ? 'text-white' : 'text-[#1F1F1F]'}`} data-id="element-2957" data-mp="27y0kcb6gopya">
        {title}
      </h3>

      <p className={`text-sm leading-relaxed mb-6 flex-1 ${highlight ? 'text-white/90' : isDark ? 'text-[#D8D8D0]' : 'text-[#3B3B3B]'}`} data-id="element-2958" data-mp="4infnk6khzn2">
        {description}
      </p>

      <Link to="/contact" className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mt-auto self-start pb-1 border-b-2 transition-colors ${highlight ? 'text-white border-white hover:border-white/50' : 'text-tealslate border-tealslate hover:border-tealslate/50'}`} data-id="element-2959" data-mp="3qwezm9doln4u">
        Inquire to Book <ArrowRight className="w-3 h-3" data-id="element-2960" data-mp="9g6scfnma4cw" />
      </Link>
    </div>;
}
export function Services() {
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2961" data-mp="edynrjnh08lu">
      {/* HERO */}
      <section className="relative min-h-[426px] md:h-[426px] flex items-center px-6 pt-[59px] md:px-10 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/vjWZMHEwqi3KaAveCCjapz/ChatGPT_Image_May_19,_2026,_11_42_45_PM.png')"
    }} data-id="element-2962" data-mp="2u64mssbtv9kh">
        {/* Soft cream overlay on the left so the copy reads cleanly */}
        <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(to right, rgba(245,245,240,0.92) 0%, rgba(245,245,240,0.85) 40%, rgba(245,245,240,0.35) 70%, rgba(245,245,240,0.1) 100%)'
      }} data-id="element-2963" data-mp="30mnzui7tf4xl" />

        <div className="relative max-w-7xl mx-auto" data-id="element-2964" data-mp="2310eqtgt891d">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-xl pt-2 md:pt-0" data-id="element-2965" data-mp="2ho1zj5k4hhuq">
            <motion.p variants={fadeUpVariants} className="text-[10px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: '#3B4A4F'
          }} data-id="element-2966" data-mp="2cvsadrqu0lti">
              Our Services
            </motion.p>
            <motion.h1 variants={fadeUpVariants} className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#1F1F1F] leading-[1.05] mb-1" data-id="element-2967" data-mp="3kaxfrbil6c17">
              Personalized support.
            </motion.h1>
            <motion.h1 variants={fadeUpVariants} className="text-3xl md:text-4xl lg:text-5xl font-serif italic leading-[1.05] mb-4" style={{
            color: '#3B4A4F'
          }} data-id="element-2968" data-mp="1xprdlgedk3g">
              Proven results.
            </motion.h1>
            <motion.p variants={fadeUpVariants} className="text-sm md:text-base text-[#3A3A33] leading-relaxed max-w-lg mb-5" data-id="element-2969" data-mp="3achv9re2e10o">
              At Structured Nutrition, our services are designed to meet you
              where you are and help you reach your health goals with
              science-backed strategies, expert guidance, and ongoing support.
            </motion.p>
            <motion.div variants={fadeUpVariants} data-id="element-2970" data-mp="14oqw98mvr17n">
              <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center justify-center gap-2 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors" style={{
              backgroundColor: '#3B4A4F'
            }} data-id="element-2971" data-mp="14rmu6wyq9pw3">
                Send a Message
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* INSURANCE CAROUSEL */}
      <InsuranceMarquee data-id="element-2972" data-mp="c0lemur4hsc1" />

      {/* WHAT WE OFFER — Services grid */}
      <section className="bg-[#F9F9F9] py-20 md:py-28 px-6" data-id="element-2973" data-mp="7zj9xyefso2o">
        <div className="max-w-7xl mx-auto" data-id="element-2974" data-mp="8144ho0r470j">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="text-center mb-16" data-id="element-2975" data-mp="26f5v0v8ev1pm">
            <motion.p variants={fadeUpVariants} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-4" style={{
            color: '#3B4A4F'
          }} data-id="element-2976" data-mp="2loez2n05qbvf">
              What We Offer
            </motion.p>
            <motion.h2 variants={fadeUpVariants} className="text-4xl md:text-5xl font-serif text-[#1F1F1F] leading-[1.1]" data-id="element-2977" data-mp="2gu0xu3ytdaiu">
              Services tailored to you
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2978" data-mp="1sy6fcqpz3jeq">
            {[{
            icon: ClipboardList,
            title: '1:1 Nutrition Coaching',
            description: 'Personalized nutrition coaching built around your goals, lifestyle, and preferences to help you achieve lasting results.'
          }, {
            icon: Salad,
            title: 'Personalized Meal Plans',
            description: 'Custom meal plans designed to fit your goals, food preferences, and lifestyle, making healthy eating simple and sustainable.'
          }, {
            icon: Dumbbell,
            title: 'Performance Nutrition',
            description: 'Nutrition strategies to fuel your workouts, enhance performance, and support recovery so you can perform at your best.'
          }, {
            icon: Activity,
            title: 'Strength Training Guidance',
            description: 'Expert training guidance to help you build muscle, increase strength, and improve body composition safely and effectively.'
          }, {
            icon: FlaskConical,
            title: 'Supplement Recommendations',
            description: 'Science-backed supplement recommendations tailored to your needs to help fill gaps and support your overall health.'
          }, {
            icon: UserCircle,
            title: 'Accountability Coaching',
            description: 'Ongoing support, check-ins, and accountability to keep you consistent, motivated, and on track with your goals.'
          }].map(({
            icon: Icon,
            title,
            description
          }) => <motion.div key={title} variants={fadeUpVariants} className="bg-white border border-[#E5E5E5] p-10 text-center flex flex-col items-center" data-id="element-2979" data-mp="3rnr1mtpxw9k4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 border" style={{
              backgroundColor: '#FFFFFF',
              borderColor: '#E5E5E5'
            }} data-id="element-2980" data-mp="o5h8c4aaal75">
                  <Icon className="w-7 h-7" style={{
                color: '#3B4A4F'
              }} strokeWidth={1.5} data-id="element-2981" data-mp="15fbd7c3638aw" />
                </div>
                <h3 className="font-serif text-xl text-[#1F1F1F] mb-4" data-id="element-2982" data-mp="1efztkdg19kqy">
                  {title}
                </h3>
                <p className="text-sm text-[#3A3A33] leading-relaxed mb-6 flex-1" data-id="element-2983" data-mp="1jdj6na0zvrhb">
                  {description}
                </p>
                <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="text-[11px] font-bold uppercase tracking-[0.25em] inline-flex items-center gap-2 transition-colors" style={{
              color: '#3B4A4F'
            }} data-id="element-2984" data-mp="1t55pnsjkquum">
                  Learn More <ArrowRight className="w-3 h-3" data-id="element-2985" data-mp="31lnt5yqgyeq" />
                </Link>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="bg-white py-20 md:py-28 px-6" data-id="element-2986" data-mp="bv8dqfg9f7g0">
        <div className="max-w-3xl mx-auto text-center" data-id="element-2987" data-mp="72f9dsope14g">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} data-id="element-2988" data-mp="70uf846awk74">
            <motion.p variants={fadeUpVariants} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-5" style={{
            color: '#3B4A4F'
          }} data-id="element-2989" data-mp="us9pqk88e7tg">
              Our Approach
            </motion.p>
            <motion.h2 variants={fadeUpVariants} className="font-serif text-4xl md:text-5xl text-[#1F1F1F] leading-[1.1] mb-2" data-id="element-2990" data-mp="2wlpne12qkmxx">
              Science-backed.
            </motion.h2>
            <motion.h2 variants={fadeUpVariants} className="font-serif italic text-4xl md:text-5xl leading-[1.1] mb-8" style={{
            color: '#3B4A4F'
          }} data-id="element-2991" data-mp="2mghf25qyho82">
              Person-centered.
            </motion.h2>
            <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#3A3A33] leading-relaxed mb-10 max-w-2xl mx-auto" data-id="element-2992" data-mp="1yqn1flytbvmr">
              We combine the latest nutrition science with a personalized
              approach to help you build sustainable habits, improve your
              health, and feel your best, without restrictive diets or
              one-size-fits-all solutions.
            </motion.p>

            <motion.ul variants={fadeUpVariants} className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-8 gap-y-4" data-id="element-2993" data-mp="3k4htt4x30ix6">
              {['Evidence-based strategies', 'Personalized to your lifestyle', 'Sustainable, realistic, and effective'].map(item => <li key={item} className="flex items-center gap-3 text-[15px] text-[#1F1F1F]" data-id="element-2994" data-mp="1f0suve4ulpqk">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full border shrink-0" style={{
                borderColor: '#3B4A4F'
              }} data-id="element-2995" data-mp="161flsmkmz36d">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#3B4A4F" strokeWidth="2" className="w-4 h-4" data-id="element-2996" data-mp="wcfhcnnd7fks">
                      <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" data-id="element-2997" data-mp="ic4ojf2hvqwz" />
                    </svg>
                  </span>
                  {item}
                </li>)}
            </motion.ul>
          </motion.div>
        </div>
      </section>

      {/* READY TO GET STARTED — final CTA strip */}
      <section className="bg-white pb-20 md:pb-28 px-6" data-id="element-2998" data-mp="37y6j43xgm0qz">
        <div className="max-w-7xl mx-auto" data-id="element-2999" data-mp="2b8yx6usxhpmj">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={fadeUpVariants} className="p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-6 justify-between" style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5'
        }} data-id="element-3000" data-mp="2ba088z9y2c49">
            <div className="flex items-start md:items-center gap-5 md:gap-6" data-id="element-3001" data-mp="1wjklhg1vqw3z">
              <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0" style={{
              backgroundColor: '#3B4A4F'
            }} data-id="element-3002" data-mp="2zv04p93px0nh">
                <Clock className="w-6 h-6 text-white" strokeWidth={1.8} data-id="element-3003" data-mp="38ntmpeajue51" />
              </div>
              <div data-id="element-3004" data-mp="34lyoiinx0gvh">
                <h3 className="font-serif text-2xl md:text-3xl text-[#1F1F1F] mb-2 leading-tight" data-id="element-3005" data-mp="34lgk2yr3mwch">
                  Ready to get started?
                </h3>
                <p className="text-sm md:text-base text-[#3A3A33] leading-relaxed max-w-lg" data-id="element-3006" data-mp="2gxyu98rea8c5">
                  Send a message to learn more about our services and find the
                  right fit for you. We respond personally within 48 hours.
                </p>
              </div>
            </div>

            <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center justify-center gap-2 px-8 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors shrink-0" style={{
            backgroundColor: '#3B4A4F'
          }} data-id="element-3007" data-mp="56l4gtbwtc9s">
              Send a Message
            </Link>
          </motion.div>
        </div>
      </section>
    </div>;
}
const INSURANCE_LIST: {
  name: string;
  domain: string;
}[] = [{
  name: 'Aetna',
  domain: 'aetna.com'
}, {
  name: 'Anthem',
  domain: 'anthem.com'
}, {
  name: 'Blue Care Network',
  domain: 'bcbsm.com'
}, {
  name: 'Blue Cross',
  domain: 'bcbs.com'
}, {
  name: 'Blue Cross Blue Shield',
  domain: 'bcbs.com'
}, {
  name: 'Blue Cross Blue Shield of Illinois',
  domain: 'bcbsil.com'
}, {
  name: 'Blue Shield',
  domain: 'blueshieldca.com'
}, {
  name: 'CareFirst',
  domain: 'carefirst.com'
}, {
  name: 'Cigna',
  domain: 'cigna.com'
}, {
  name: 'Empire',
  domain: 'empireblue.com'
}, {
  name: 'Florida Blue',
  domain: 'floridablue.com'
}, {
  name: 'Golden Rule',
  domain: 'goldenrule.com'
}, {
  name: 'Highmark',
  domain: 'highmark.com'
}, {
  name: 'Horizon',
  domain: 'horizonblue.com'
}, {
  name: 'Independence Blue Cross',
  domain: 'ibx.com'
}, {
  name: 'Optum',
  domain: 'optum.com'
}, {
  name: 'Oxford',
  domain: 'oxfordhealth.com'
}, {
  name: 'Regence Blue Cross Blue Shield',
  domain: 'regence.com'
}, {
  name: 'United Healthcare',
  domain: 'uhc.com'
}, {
  name: 'United Medical Resources',
  domain: 'umr.com'
}];
function InsuranceItem({
  name,
  domain
}: {
  name: string;
  domain: string;
}) {
  const [errored, setErrored] = useState(false);
  return <div className="shrink-0 flex items-center justify-center px-8 md:px-10 border-r border-white/30 h-10 md:h-12" data-id="element-3008" data-mp="3g60w4gh2sj6o">
      {errored ? <span className="text-lg md:text-xl font-medium text-white whitespace-nowrap" data-id="element-3009" data-mp="29odt0c92nnie">
          {name}
        </span> : <img src={`https://logo.clearbit.com/${domain}`} alt={name} onError={() => setErrored(true)} className="h-full w-auto object-contain" style={{
      filter: 'brightness(0) invert(1)',
      opacity: 0.9
    }} data-id="element-3010" data-mp="ka4q59d713mm" />}
    </div>;
}
function InsuranceMarquee() {
  // Duplicate the list so the track can loop seamlessly.
  const track = [...INSURANCE_LIST, ...INSURANCE_LIST];
  return <section className="bg-[#2A3A3F] py-7 md:py-9 overflow-hidden" data-id="element-3011" data-mp="132guu2rbkgzx">
      <div className="max-w-7xl mx-auto px-6 mb-4 text-center" data-id="element-3012" data-mp="17xmesw15vlld">
        <p className="text-[10px] md:text-[11px] font-bold text-white/60 uppercase tracking-[0.3em]" data-id="element-3013" data-mp="1cy0n8f6ocxjx">
          Insurance Accepted
        </p>
      </div>
      <div className="relative w-full overflow-hidden group" style={{
      maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
      WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)'
    }} data-id="element-3014" data-mp="1gxrgcpjobprm">
        <motion.div className="flex w-max" animate={{
        x: ['0%', '-50%']
      }} transition={{
        duration: 120,
        ease: 'linear',
        repeat: Infinity
      }} style={{
        willChange: 'transform'
      }} data-id="element-3015" data-mp="37j20np6zxm9d">
          {track.map((item, i) => <InsuranceItem key={`${item.name}-${i}`} name={item.name} domain={item.domain} data-id="element-3016" data-mp="3m87u0wj5ki9c" data-mpi={i} />)}
        </motion.div>
      </div>
    </section>;
}